import './Help.css';

/**
 * Help & Support — AI Readiness.
 *
 * CLONED from `ai_readiness_template/views/help-support.ejs` by way of Inventory
 * Registry's port of the same screen, which the owner pointed at as the design to
 * follow (2026-08-11: "Use APR module for both of these as a reference. This should
 * be the same design").
 *
 * IDENTICAL to that screen except for two things, and deliberately so:
 *   · the subtitle names THIS product, which is the route/label rewrite CLAUDE.md
 *     §1.2 permits;
 *   · the root class is `air-help`, because stylesheets are global once bundled.
 *
 * NOT shared as one component: products do not share code between their folders
 * (CLAUDE.md §1.7), which is why Inventory Registry has its own copy despite APR
 * already having one. The contacts, the four department cards and all sixteen
 * bullets are the source's, verbatim.
 */

interface Contact {
  photo: string;
  initials: string;
  label: string;
  name: string;
  email: string;
  phoneHref: string;
  phoneText: string;
  primary?: boolean;
}

const CONTACTS: Contact[] = [
  {
    photo: '/images/Goran_Lucic.png',
    initials: 'GL',
    label: 'Primary Contact',
    name: 'Goran Lucic',
    email: 'Goran.Lucic@talogent.com',
    phoneHref: '+14045470589',
    phoneText: '+1.404.547.0589',
    primary: true,
  },
  {
    photo: '/images/Joe_Schuller.jpeg',
    initials: 'JS',
    label: 'Contact',
    name: 'Joe Schuller',
    email: 'Joe.Schuller@talogent.com',
    phoneHref: '+16784785014',
    phoneText: '+1.678.478.5014',
  },
];

interface Department {
  kind: 'support' | 'sales' | 'marketing' | 'training';
  icon: string;
  title: string;
  desc: string;
  features: string[];
}

/** The four cards and all sixteen bullets, verbatim from the source. */
const DEPARTMENTS: Department[] = [
  {
    kind: 'support',
    icon: 'chat_bubble_outline',
    title: 'Technical Support',
    desc: 'Get help with technical issues, bugs, and system problems',
    features: [
      'Bug Reports & Issue Resolution',
      'System Configuration Assistance',
      'Performance Troubleshooting',
      'Integration Support',
    ],
  },
  {
    kind: 'sales',
    icon: 'attach_money',
    title: 'Sales',
    desc: 'Inquiries about licensing, pricing, and enterprise solutions',
    features: [
      'Enterprise Licensing Options',
      'Custom Pricing Packages',
      'Volume Discounts',
      'Subscription Management',
    ],
  },
  {
    kind: 'marketing',
    icon: 'star_outline',
    title: 'Marketing',
    desc: 'Partnership opportunities, media inquiries, and promotions',
    features: [
      'Strategic Partnerships',
      'Media & Press Inquiries',
      'Case Studies & Success Stories',
      'Co-Marketing Opportunities',
    ],
  },
  {
    kind: 'training',
    icon: 'school',
    title: 'Training & Enablement',
    desc: 'Comprehensive training programs for success',
    features: [
      'Platform Training Sessions',
      'Best Practices Workshops',
      'Custom Documentation',
      'Team Onboarding Programs',
    ],
  },
];

export function HelpSupport() {
  return (
    <div className="air-help">
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Help &amp; Support</h1>
            <p className="page-header__subtitle">
              Get assistance with the AI Readiness platform - we&apos;re here to help
            </p>
          </div>
          <div className="page-header__actions" />
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="help-card">
        <div className="help-card-header">
          <h2>Contact Information</h2>
        </div>
        <div className="help-card-content">
          <div className="contacts-grid">
            {CONTACTS.map((c) => (
              <div className={`contact-card${c.primary ? ' primary' : ''}`} key={c.name}>
                {/*
                  The source falls back to an initials disc if the image fails — the same
                  `onerror` swap it does inline, done here by hiding the <img> and revealing
                  the placeholder underneath it.
                */}
                <img
                  src={c.photo}
                  alt={c.name}
                  className="contact-avatar"
                  onError={(e) => {
                    const img = e.currentTarget;
                    img.style.display = 'none';
                    const ph = img.nextElementSibling as HTMLElement | null;
                    if (ph) ph.style.display = 'flex';
                  }}
                />
                <div className="contact-avatar-placeholder" style={{ display: 'none' }}>
                  {c.initials}
                </div>
                <div className="contact-info">
                  <div className="contact-label">{c.label}</div>
                  <div className="contact-name">{c.name}</div>
                  <a href={`mailto:${c.email}`} className="contact-detail">
                    {c.email}
                  </a>
                  <a href={`tel:${c.phoneHref}`} className="contact-detail">
                    {c.phoneText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Department Contacts Section */}
      <div className="help-card">
        <div className="help-card-header">
          <h2>Department Contacts</h2>
        </div>
        <div className="help-card-content">
          <div className="departments-grid">
            {DEPARTMENTS.map((d) => (
              <div className="department-card" key={d.title}>
                <div className="department-header">
                  <div className={`department-icon ${d.kind}`}>
                    <i className="material-icons">{d.icon}</i>
                  </div>
                  <div className="department-header-text">
                    <div className="department-title">{d.title}</div>
                    <div className="department-desc">{d.desc}</div>
                  </div>
                </div>
                <ul className="card-features">
                  {d.features.map((f) => (
                    <li key={f}>
                      <i className={`material-icons ${d.kind}-check`}>check_circle</i>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/*
        NEED ADDITIONAL HELP — the owner's addition (2026-08-08), taken from
        `apr/settings/HelpSupport.tsx` at its own values rather than designed here. One call
        to action for anything the department cards above do not cover.
      */}
      <div className="help-card">
        <div className="help-card-content">
          <div className="need-help-cta">
            <svg
              className="need-help-icon"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
            </svg>
            <h3>Need Additional Help?</h3>
            <p>Our support team is ready to assist you with any questions.</p>
            <a href="mailto:support@talogent.com" className="contact-support-btn">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
